import { produce } from 'immer';

/**
 * Structure des données affichées :
 * {
 *   [variableId]: {
 *     groupId?: string, // undefined si variable individuelle (non groupée)
 *     sectionIds: string[] // IDs des sections qui utilisent cette variable
 *   }
 * }
 */
export const displayedVariablesActions = (set, get) => ({
  displayedVariables: {},

  /**
   * Ajouter une variable affichée
   * @param {string} variableId - ID de la variable
   * @param {string} sectionId - ID de la section qui utilise cette variable
   * @param {string} [groupId] - ID du groupement si la variable appartient à un groupement
   */
  addDisplayedVariable: (variableId, sectionId, groupId = undefined) => {
    set(
      produce((draft) => {
        if (!draft.displayedVariables[variableId]) {
          draft.displayedVariables[variableId] = {
            groupId,
            sectionIds: [],
          };
        }
        if (!draft.displayedVariables[variableId].sectionIds.includes(sectionId)) {
          draft.displayedVariables[variableId].sectionIds.push(sectionId);
        }
      }),
      false,
      `addDisplayedVariable ${variableId} from section ${sectionId}`
    );
  },

  /**
   * Retirer une variable affichée (quand une section est désactivée)
   * @param {string} variableId - ID de la variable
   * @param {string} sectionId - ID de la section qui n'utilise plus cette variable
   * @returns {boolean} - true si la variable doit être complètement retirée (plus aucune section ne l'utilise)
   */
  removeDisplayedVariable: (variableId, sectionId) => {
    let shouldRemove = false;
    set(
      produce((draft) => {
        if (draft.displayedVariables[variableId]) {
          draft.displayedVariables[variableId].sectionIds = draft.displayedVariables[variableId].sectionIds.filter(
            (id) => id !== sectionId
          );
          // Si plus aucune section n'utilise cette variable, la retirer complètement
          if (draft.displayedVariables[variableId].sectionIds.length === 0) {
            delete draft.displayedVariables[variableId];
            shouldRemove = true;
          }
        }
      }),
      false,
      `removeDisplayedVariable ${variableId} from section ${sectionId}`
    );
    return shouldRemove;
  },

  /**
   * Retirer toutes les variables d'une section
   * @param {string} sectionId - ID de la section à retirer
   * @returns {string[]} - Liste des IDs de variables qui doivent être complètement retirées
   */
  removeSectionVariables: (sectionId) => {
    const variablesToRemove = [];
    set(
      produce((draft) => {
        Object.keys(draft.displayedVariables).forEach((variableId) => {
          if (draft.displayedVariables[variableId].sectionIds.includes(sectionId)) {
            draft.displayedVariables[variableId].sectionIds = draft.displayedVariables[variableId].sectionIds.filter(
              (id) => id !== sectionId
            );
            // Si plus aucune section n'utilise cette variable, la marquer pour suppression
            if (draft.displayedVariables[variableId].sectionIds.length === 0) {
              variablesToRemove.push(variableId);
              delete draft.displayedVariables[variableId];
            }
          }
        });
      }),
      false,
      `removeSectionVariables ${sectionId}`
    );
    return variablesToRemove;
  },

  /**
   * Vérifier si une variable est déjà affichée
   * @param {string} variableId - ID de la variable
   * @returns {boolean}
   */
  isVariableDisplayed: (variableId) => {
    return !!get().displayedVariables[variableId];
  },

  /**
   * Obtenir le groupement associé à une variable
   * @param {string} variableId - ID de la variable
   * @returns {string|undefined} - ID du groupement ou undefined
   */
  getVariableGroup: (variableId) => {
    return get().displayedVariables[variableId]?.groupId;
  },

  /**
   * Obtenir les sections qui utilisent une variable
   * @param {string} variableId - ID de la variable
   * @returns {string[]} - Liste des IDs de sections
   */
  getVariableSections: (variableId) => {
    return get().displayedVariables[variableId]?.sectionIds || [];
  },

  /**
   * Obtenir toutes les variables d'un groupement
   * @param {string} groupId - ID du groupement
   * @returns {string[]} - Liste des IDs de variables
   */
  getGroupVariables: (groupId) => {
    return Object.keys(get().displayedVariables).filter(
      (variableId) => get().displayedVariables[variableId].groupId === groupId
    );
  },

  /**
   * Réinitialiser les variables affichées (utile lors du changement de document)
   */
  resetDisplayedVariables: () => {
    set(
      produce((draft) => {
        draft.displayedVariables = {};
      }),
      false,
      'resetDisplayedVariables'
    );
  },
});
